import { Config, EditableComponent } from '@adobe/aem-react-editable-components';
import { PropertyBar } from './PropertyBar';
import { PropertyBarAEMProps, PropertyBarWrapperProps } from './PropertyBar.types';
import { StyledPropertyBarContainer, StyledPropertyBarPlaceholder } from './PropertyBar.styles';
import { useScrollStore, useStickyState } from '@marriott/mi-store-utils';
import { useCallback, useEffect, useRef, useState } from 'react';

export const ProertyBarConfig: Config<PropertyBarAEMProps> = {
  emptyLabel: 'Property Bar',
  isEmpty: () => false,
  resourceType: `${process.env['NEXT_PUBLIC_AEM_SITE']}/components/content/propertybar`,
};
export const PropertyBarWrapper = (props: PropertyBarWrapperProps) => {
  const { model, locale } = props;
  const componentId = model?.componentId;
  const [isServiceDown, setIsServiceDown] = useState(false);
  const isAuthorMode = props.isAuthorMode;

  const setRegisteredComponent = useScrollStore(state => state.setRegisteredComponent);
  const navComponentOrder = useScrollStore(state => state.navComponentOrder);
  const scrollPosition = useScrollStore(state => state.scrollPosition);
  const scrollDirection = useScrollStore(state => state.scrollDirection);
  const prevScrollDirection = useScrollStore(state => state.prevScrollDirection);
  const { isSticky, top } = useStickyState((componentId as string) || '');
  const propertyBarRef = useRef<HTMLDivElement | null>(null);
  const [placeholderHeight, setPlaceholderHeight] = useState(0);
  const isStickyOnScrollUp = !!model?.scrollSetting?.isStickyOnScrollUp;
  const isStickyOnScrollDown = !!model?.scrollSetting?.isStickyOnScrollDown;
  const isScrollingUp = scrollDirection === 'up' || (scrollDirection === '' && prevScrollDirection === 'up');
  const isScrollingDown = scrollDirection === 'down' || (scrollDirection === '' && prevScrollDirection === 'down');
  const EDGE_TOLERANCE_PX = 2;
  const isAtBottom =
    typeof window !== 'undefined' &&
    scrollPosition >= Math.max(0, document.documentElement.scrollHeight - window.innerHeight) - EDGE_TOLERANCE_PX;
  const sticky = isSticky;
  const stickyTop = top;

  const registerPropertyBarComponent = useCallback(
    (ref: HTMLDivElement) => {
      if (!ref || !componentId) {
        return;
      }
      setRegisteredComponent({
        componentId,
        isStickyOnScrollUp,
        isStickyOnScrollDown,
        height: ref.clientHeight,
      });
      setPlaceholderHeight(ref.clientHeight);
    },
    [componentId, isStickyOnScrollDown, isStickyOnScrollUp, setRegisteredComponent]
  );

  const containerRef = useCallback(
    (ref: HTMLDivElement) => {
      if (!ref) {
        return;
      }
      propertyBarRef.current = ref;
      registerPropertyBarComponent(ref);
    },
    [registerPropertyBarComponent]
  );

  useEffect(() => {
    if (!propertyBarRef.current || !componentId || navComponentOrder.length === 0) {
      return;
    }
    registerPropertyBarComponent(propertyBarRef.current);
  }, [componentId, navComponentOrder.length, registerPropertyBarComponent]);

  useEffect(() => {
    if (!propertyBarRef.current || !componentId || typeof ResizeObserver === 'undefined') {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      if (propertyBarRef.current) {
        registerPropertyBarComponent(propertyBarRef.current);
      }
    });
    resizeObserver.observe(propertyBarRef.current);
    return () => resizeObserver.disconnect();
  }, [componentId, registerPropertyBarComponent]);

  if (isServiceDown && !isAuthorMode) {
    return null;
  }

  return (
    <div>
      {props.cqPath?.includes('experiencefragment') ? (
        <StyledPropertyBarContainer
          data-component-name="o-common-static-propertybar"
          className="m-container-fullbleed"
          ref={containerRef}
          top={stickyTop}
          isSticky={sticky}
          shouldAnimateTop={sticky && isScrollingUp}
          shouldAnimateBottom={sticky && isScrollingDown && !isAtBottom}
        >
          <PropertyBar
            locale={locale}
            trackingProperties={model.trackingProperties}
            {...model.resourceProperties}
            onServiceError={() => setIsServiceDown(true)}
          />
        </StyledPropertyBarContainer>
      ) : (
        <EditableComponent config={ProertyBarConfig} {...props}>
          <StyledPropertyBarContainer
            data-component-name="o-common-static-propertybar"
            ref={containerRef}
            top={stickyTop}
            isSticky={sticky}
            shouldAnimateTop={sticky && isScrollingUp}
            shouldAnimateBottom={sticky && isScrollingDown && !isAtBottom}
            className="m-container-fullbleed"
          >
            <PropertyBar
              locale={locale}
              trackingProperties={model.trackingProperties}
              {...model.resourceProperties}
              onServiceError={() => setIsServiceDown(true)}
            />
          </StyledPropertyBarContainer>
        </EditableComponent>
      )}
      {sticky && <StyledPropertyBarPlaceholder height={placeholderHeight} />}
    </div>
  );
};
